import { type FC, useCallback, useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { focusManager } from '@tanstack/react-query'
import type { Interaction } from './interaction.js'
import { InteractionErrorBoundary } from './InteractionErrorBoundary.js'
import { InteractionProvider } from './InteractionProvider.js'
import { useInteractionConfig } from './useInteractionConfig.js'
import { useGetProfileCompletedInteractions } from '../profile/model/profile/hooks.js'
import { isFeatureEnabled } from '../config/index.js'

export const InteractionManager: FC = () => {
  const [currentInteraction, setCurrentInteraction] = useState<Interaction | null>(null)
  const [isOpen, setOpen] = useState(false)
  const [revalidationCount, setRevalidationCount] = useState(0)
  const { isFetched } = useGetProfileCompletedInteractions()
  const { interactions: configuredInteractions } = useInteractionConfig()
  const isEnabled = isFeatureEnabled('interaction')

  const handleDismiss = useCallback(() => {
    setOpen(false)
    setRevalidationCount((count) => count + 1)
  }, [])

  /**
   * React Query revalidates its queries when the window regains focus. An interaction that was not relevant on page
   * load can become relevant after such a revalidation, so we bump a counter to re-run the determination below.
   */
  useEffect(
    () =>
      focusManager.subscribe((isFocused) => {
        if (isFocused) {
          setRevalidationCount((count) => count + 1)
        }
      }),
    []
  )

  /**
   * Based on the order from the configuration, we determine what the next interaction should be.
   * The provided `shouldExecute` function is used to assess whether the respective interaction should be displayed.
   * If the function does not yield a result, we proceed to the next interaction in the configuration.
   *
   * This runs once the data for previously `completedInteractions` has been retrieved from the API, and again on
   * every revalidation. An interaction that was completed in the meantime no longer qualifies, which is what allows
   * a next one to take its place.
   */
  useEffect(() => {
    let isOutdated = false

    const determineCurrentInteraction = async () => {
      const result = await configuredInteractions.reduce(async (interaction, action) => {
        const resolvedInteraction = await interaction
        if (resolvedInteraction) {
          return resolvedInteraction
        }
        const shouldExecute = await action.shouldExecute()
        return shouldExecute ? action : null
      }, Promise.resolve(null))

      if (isOutdated) {
        return
      }

      if (result) {
        setCurrentInteraction(result)
      }
      // A dismissed interaction stays mounted so it can animate away; only its visibility is turned off here.
      setOpen(result !== null)
    }

    if (isFetched && isEnabled) {
      determineCurrentInteraction()
    }

    // A revalidation can start a newer determination, which supersedes the one still running here.
    return () => {
      isOutdated = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched, isEnabled, revalidationCount])

  return (
    <>
      <Outlet />
      {/* Keyed so a crashed interaction does not leave the slot permanently disabled for the next one. */}
      <InteractionErrorBoundary key={currentInteraction?.type}>
        <InteractionProvider isOpen={isOpen} onDismiss={handleDismiss}>
          {currentInteraction?.component}
        </InteractionProvider>
      </InteractionErrorBoundary>
    </>
  )
}
